import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { t } from "@/i18n";
import { Button, Card, Select, Badge, Spinner, EmptyState } from "@/components/ui";
import {
  useClassroomConfig,
  useClassrooms,
  useConnections,
  useDisconnectConnection,
  useSyncConnection,
  useUpdateClassroom,
  useUpdateConnectionLabel,
  type Connection,
  type Classroom,
} from "@/lib/queries";

export const Route = createFileRoute("/app/classes")({
  validateSearch: (search: Record<string, unknown>) => ({
    connected: search.connected === "1",
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: ClassesPage,
});

function ClassesPage() {
  const { connected, error } = Route.useSearch();
  const config = useClassroomConfig();
  const connections = useConnections();
  const classrooms = useClassrooms();
  const sync = useSyncConnection();
  const disconnect = useDisconnectConnection();

  const grouped = new Map<string, { conn: Connection; rooms: Classroom[] }>();
  for (const c of connections.data ?? []) grouped.set(c.id, { conn: c, rooms: [] });
  for (const room of classrooms.data ?? []) {
    grouped.get(room.googleConnectionId)?.rooms.push(room);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("classes.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("classes.subtitle")}</p>
        </div>
        <a href="/api/classroom/connect/google">
          <Button disabled={!config.data?.configured}>🎓 {t("classes.connect")}</Button>
        </a>
      </div>

      {!config.data?.configured && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          {t("classes.notConfigured")}
        </div>
      )}
      {connected && (
        <div className="rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
          ✓ {t("classes.connectedBanner")}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
          {t(`classes.connectError.${error}`, { defaultValue: t("classes.connectError.generic") })}
        </div>
      )}

      {connections.isLoading ? (
        <Spinner label={t("app.loading")} />
      ) : (connections.data ?? []).length === 0 ? (
        <EmptyState>{t("classes.noConnections")}</EmptyState>
      ) : (
        [...grouped.values()].map(({ conn, rooms }) => (
          <ConnectionCard
            key={conn.id}
            conn={conn}
            rooms={rooms}
            syncing={sync.isPending}
            onSync={() => sync.mutate(conn.id)}
            onDisconnect={() => {
              if (window.confirm(t("classes.disconnectConfirm"))) disconnect.mutate(conn.id);
            }}
          />
        ))
      )}
    </div>
  );
}

function ConnectionCard({
  conn,
  rooms,
  syncing,
  onSync,
  onDisconnect,
}: {
  conn: Connection;
  rooms: Classroom[];
  syncing: boolean;
  onSync: () => void;
  onDisconnect: () => void;
}) {
  const label = useUpdateConnectionLabel();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(conn.label);

  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                className="rounded-md border px-2 py-1 text-sm"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <Button
                onClick={async () => {
                  await label.mutateAsync({ id: conn.id, label: value.trim() || conn.googleEmail });
                  setEditing(false);
                }}
              >
                {t("common.save")}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                {t("common.cancel")}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-semibold">{conn.label}</span>
              <Badge tone="green">✓ {t("classes.connected")}</Badge>
              <Button variant="ghost" onClick={() => setEditing(true)}>
                ✎ {t("classes.rename")}
              </Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{conn.googleEmail}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={syncing} onClick={onSync}>
            ⟳ {syncing ? t("common.saving") : t("classes.sync")}
          </Button>
          <Button variant="danger" onClick={onDisconnect}>
            {t("classes.disconnect")}
          </Button>
        </div>
      </div>

      {rooms.length === 0 ? (
        <EmptyState>{t("classes.noClassrooms")}</EmptyState>
      ) : (
        <ul className="divide-y">
          {rooms.map((room) => (
            <ClassroomRow key={room.id} room={room} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ClassroomRow({ room }: { room: Classroom }) {
  const update = useUpdateClassroom();
  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium">{room.name}</p>
        <p className="text-xs text-muted-foreground">
          {room.section ? `${room.section} · ` : ""}
          {t("classes.lastSynced")}:{" "}
          {room.lastSyncedAt ? new Date(room.lastSyncedAt).toLocaleString() : t("classes.never")}
        </p>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t("classes.language")}</span>
        <Select
          value={room.languageCode}
          disabled={update.isPending}
          onChange={(e) => update.mutate({ id: room.id, languageCode: e.target.value })}
        >
          <option value="en">English</option>
          <option value="es">Español</option>
        </Select>
      </label>
    </li>
  );
}